import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useStore } from '../store/useStore';
import { parseOutline } from '../lib/outline';
import { getDocument } from '../lib/library';
import { backlinksFor } from '../lib/linkIndex';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useT } from '../i18n/useT';
import { Icon } from './Icon';

/**
 * Table-of-contents panel derived from the active document's headings. Clicking
 * an entry scrolls both editor and preview to that source line (via the
 * `goToLine` callback EditorPage registers).
 */
export function OutlinePanel() {
  const t = useT();
  const controller = useDocControllerContext();
  const open = useStore((s) => s.outlineOpen);
  const content = useStore((s) => s.activeDoc.content);
  const activeId = useStore((s) => s.activeDoc.id);
  const goToLine = useStore((s) => s.goToLine);
  const setOutlineOpen = useStore((s) => s.setOutlineOpen);

  const items = useMemo(() => parseOutline(content), [content]);
  // Served by the link index rather than by scanning every document's text, and
  // keyed on the document id so typing does not re-run the query.
  const backlinks = useLiveQuery(
    async () => {
      if (!activeId) return [];
      const doc = await getDocument(activeId);
      return doc ? backlinksFor(doc) : [];
    },
    [activeId],
    [],
  );

  if (!open) return null;

  return (
    <aside className="mb-outline" aria-label={t('outline.title')}>
      <div className="mb-outline-head">
        <span>{t('outline.title')}</span>
        <button
          className="mb-outline-close"
          onClick={() => setOutlineOpen(false)}
          aria-label={t('outline.close')}
          title={t('outline.close')}
        >
          <Icon name="x" size={14} />
        </button>
      </div>
      {items.length === 0 ? (
        <p className="mb-outline-empty">{t('outline.empty')}</p>
      ) : (
        <nav className="mb-outline-list">
          {items.map((h, i) => (
            <button
              key={`${h.line}-${i}`}
              className="mb-outline-item"
              style={{ paddingLeft: `${0.5 + (h.level - 1) * 0.85}rem` }}
              data-level={h.level}
              onClick={() => goToLine?.(h.line)}
              title={h.text}
            >
              {h.text}
            </button>
          ))}
        </nav>
      )}

      <div className="mb-backlinks-head">{t('outline.backlinks')}</div>
      {backlinks.length === 0 ? (
        <p className="mb-outline-empty">{t('outline.noBacklinks')}</p>
      ) : (
        <nav className="mb-outline-list">
          {backlinks.map((d) => (
            <button
              key={d.id}
              className="mb-outline-item mb-backlink-item"
              onClick={() => void controller.open(d.id)}
              title={d.title || t('common.untitled')}
            >
              <Icon name="arrow-left" size={13} />
              {d.title || t('common.untitled')}
            </button>
          ))}
        </nav>
      )}
    </aside>
  );
}
